/**
 * Formatting + the shared document-row view model.
 *
 * `DocRow` is the lightweight shape every list/view/editor screen renders;
 * screens build it by mapping live billing-service responses. (No sample data
 * lives here — the app is fully backend-driven.)
 */
import { DocStatus, DocType } from '../schema/billing/common';

export interface DocRow {
  id: string;
  type: DocType;
  number: string;
  customer: string;
  sub: string;
  date: string;
  due: string;
  status: DocStatus;
  amount: number;
  balance: number;
  ai?: boolean;
}

export const initials = (name: string) =>
  (name || '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase() || '—';

/**
 * Money formatting, via @softknack/globalization-kit (CONTRACT C-5.4, C-6.1).
 *
 * These were `fmtINR`/`fmtINRShort`: a hardcoded ₹, a 2-decimal assumption and
 * lakh/crore grouping applied to every amount regardless of currency. An AED
 * invoice rendered as "₹1,23,456". The names are kept so the ~85 existing call
 * sites keep working; the behaviour is now currency- and locale-correct.
 *
 * Currency comes from the DOCUMENT (`billing_documents.currency`, stamped and
 * immutable on the server), never from a constant — so a 3-decimal dinar and a
 * 0-decimal yen render correctly, and Indian grouping cannot leak onto AED.
 */
import { formatMoney, type CurrencyInfo, type Money as KitMoney } from '@softknack/globalization-kit/format';

/** Currency facts as published in registry.currency. Seeded for the currencies billing issues in today. */
const CURRENCY_FACTS: Record<string, CurrencyInfo> = {
  INR: { currencyCode: 'INR', minorUnitExponent: 2, symbol: '₹', symbolPosition: 'prefix', symbolSpaced: false, negativeDisplay: 'minus' },
  AED: { currencyCode: 'AED', minorUnitExponent: 2, symbol: 'AED', symbolPosition: 'prefix', symbolSpaced: true, negativeDisplay: 'minus' },
  GBP: { currencyCode: 'GBP', minorUnitExponent: 2, symbol: '£', symbolPosition: 'prefix', symbolSpaced: false, negativeDisplay: 'minus' },
  USD: { currencyCode: 'USD', minorUnitExponent: 2, symbol: '$', symbolPosition: 'prefix', symbolSpaced: false, negativeDisplay: 'minus' },
};

/** The locale each currency's amounts are grouped in. en-IN is what makes lakh/crore appear — for INR only. */
const CURRENCY_LOCALE: Record<string, string> = {
  INR: 'en-IN', AED: 'en-AE', GBP: 'en-GB', USD: 'en-US',
};

/** Tenant default, used where a screen aggregates across documents and has no single currency. */
export const DEFAULT_CURRENCY = 'INR';

const factsFor = (code: string): CurrencyInfo =>
  CURRENCY_FACTS[code] ?? {
    // Unknown currency: show the ISO code rather than guessing a symbol.
    currencyCode: code, minorUnitExponent: 2, symbol: code,
    symbolPosition: 'prefix', symbolSpaced: true, negativeDisplay: 'minus',
  };

/** A major-unit number (what the API returns as a decimal string) as kit minor units. */
const toMinor = (n: number, exponent: number): KitMoney['minor'] =>
  BigInt(Math.round(n * 10 ** exponent)).toString();

/**
 * Format an amount in its document's currency. `currency` defaults to INR only
 * so untouched call sites behave as before; pass the document's currency.
 */
export const fmtMoney = (n: number, currency: string = DEFAULT_CURRENCY): string => {
  const facts = factsFor(currency);
  return formatMoney(
    { minor: toMinor(n, facts.minorUnitExponent), currencyCode: currency },
    facts,
    CURRENCY_LOCALE[currency] ?? 'en',
  );
};

/** @deprecated Pass the document's currency: `fmtMoney(n, doc.currency)`. */
export const fmtINR = (n: number): string => fmtMoney(n, DEFAULT_CURRENCY);

/**
 * Compact form for KPI tiles. Lakh/crore are an INDIAN convention (C-5.3), so
 * they apply to INR only; other currencies get K/M, and neither ever appears on
 * a document or an export.
 */
export const fmtMoneyShort = (n: number, currency: string = DEFAULT_CURRENCY): string => {
  const abs = Math.abs(n);
  const facts = factsFor(currency);
  const sign = n < 0 ? '-' : '';
  const sym = facts.symbol + (facts.symbolSpaced ? ' ' : '');
  const trim = (v: number, unit: number) => (v / unit).toFixed(v % unit === 0 ? 0 : 1);

  if (currency === 'INR') {
    if (abs >= 1e7) return `${sign}${sym}${trim(abs, 1e7)}Cr`;
    if (abs >= 1e5) return `${sign}${sym}${trim(abs, 1e5)}L`;
  } else {
    if (abs >= 1e9) return `${sign}${sym}${trim(abs, 1e9)}B`;
    if (abs >= 1e6) return `${sign}${sym}${trim(abs, 1e6)}M`;
    if (abs >= 1e3) return `${sign}${sym}${trim(abs, 1e3)}K`;
  }
  return fmtMoney(n, currency);
};

/** @deprecated Pass the document's currency: `fmtMoneyShort(n, doc.currency)`. */
export const fmtINRShort = (n: number): string => fmtMoneyShort(n, DEFAULT_CURRENCY);

/** Parse a backend decimal-string Money value into a number for display math. */
export const money = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};
